/**
 * Login Page Component
 * Provides login interface with email/password and social login options
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { signInWithEmail, signInWithSocial } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import './LoginPage.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  // Get the page user was trying to access
  const from = location.state?.from?.pathname || '/messages';

  /**
   * Handle email/password login
   */
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmail(email, password);
      await refreshUser();
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle social login
   */
  const handleSocialLogin = async (provider) => {
    setError('');
    setLoading(true);

    try {
      await signInWithSocial(provider);
      // OAuth flow will redirect, so no need to navigate here
    } catch (err) {
      console.error(`${provider} login error:`, err);
      setError(`Failed to sign in with ${provider}. Please try again.`);
      setLoading(false);
    }
  };

  return (
    <Container className="login-page">
      <div className="login-container">
        <Card className="login-card">
          <Card.Body>
            <div className="text-center mb-4">
              <img 
                src="/ratemytalk150.png" 
                alt="RateMyTalk Logo" 
                className="login-logo mb-3"
              />
              <h2>Welcome Back</h2>
              <p className="text-muted">Sign in to access your messages</p>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleEmailLogin}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Email Address
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>
                  <FontAwesomeIcon icon={faLock} className="me-2" />
                  Password
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Check 
                  type="checkbox" 
                  label="Remember me" 
                  disabled={loading}
                />
                <Button 
                  variant="link" 
                  className="p-0"
                  onClick={() => navigate('/forgot-password')}
                  disabled={loading}
                >
                  Forgot password?
                </Button>
              </div>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Form>

            <div className="divider">
              <span>OR</span>
            </div>

            <div className="social-login">
              <Button
                variant="outline-danger"
                className="w-100 mb-2"
                onClick={() => handleSocialLogin('Google')}
                disabled={loading}
              >
                <i className="fab fa-google me-2"></i>
                Continue with Google
              </Button>

              <Button
                variant="outline-primary"
                className="w-100"
                onClick={() => handleSocialLogin('Facebook')}
                disabled={loading}
              >
                <i className="fab fa-facebook me-2"></i>
                Continue with Facebook
              </Button>
            </div>

            <div className="text-center mt-4">
              <p className="text-muted">
                Don't have an account?{' '}
                <Button 
                  variant="link" 
                  className="p-0"
                  onClick={() => navigate('/signup')}
                  disabled={loading}
                >
                  Sign up
                </Button>
              </p>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default LoginPage;
